export type InviteLandingStatus =
    | 'accepted'
    | 'already_member'
    | 'already_accepted'
    | 'pending_registration'
    | 'revoked'
    | 'expired'
    | 'not_found'
    | 'no_seats';

export type InviteAuthMethod =
    | 'email'
    | 'google'
    | 'workos'
    | 'register'
    | 'workos_register';

export interface IInviteLandingWorkspace {
    name: string;
    short_name: string;
    workosWorkspace: boolean;
    adminEmail?: string | null;
}

export interface IInviteLandingInvite {
    email: string;
    workspace: IInviteLandingWorkspace | null;
}

export interface IInviteLandingResponse {
    status: InviteLandingStatus;
    message: string;
    accepted: boolean;
    user: 'exists' | 'new' | null;
    authMethod: InviteAuthMethod | null;
    invite: IInviteLandingInvite | null;
}

export interface IInviteAuthContext {
    inviteCode: string;
    landing: IInviteLandingResponse;
}

export const INVITE_SUCCESS_STATUSES: InviteLandingStatus[] = [
    'accepted',
    'already_member',
    'already_accepted',
];

export const INVITE_ERROR_STATUSES: InviteLandingStatus[] = [
    'revoked',
    'expired',
    'not_found',
    'no_seats',
];

export const isInviteLandingResponse = (
    value: unknown,
): value is IInviteLandingResponse => {
    return !!value && typeof value === 'object' && 'status' in value;
};

/** User must create a LeadDelta account before joining the invited workspace. */
export const isInvitePendingRegistration = (landing: IInviteLandingResponse): boolean => {
    if (INVITE_ERROR_STATUSES.includes(landing.status)) return false;
    if (landing.status === 'pending_registration') return true;
    if (landing.user === 'new') return true;
    if (landing.authMethod === 'register' || landing.authMethod === 'workos_register') return true;
    return false;
};

/** Existing user — show invite confirmation and route to login. */
export const isInviteLoginLanding = (landing: IInviteLandingResponse): boolean => {
    return INVITE_SUCCESS_STATUSES.includes(landing.status) && !isInvitePendingRegistration(landing);
};

export const isInviteSsoRegistration = (landing: IInviteLandingResponse): boolean => {
    return Boolean(
        landing.invite?.workspace?.workosWorkspace ||
        landing.authMethod === 'workos' ||
        landing.authMethod === 'workos_register',
    );
};

export const SSO_INVITE_CODE_SESSION_KEY = 'sso_invite_code';

export const INVITE_PENDING_VERIFICATION_KEY = 'invitePendingVerification';

export interface IInvitePendingVerification {
    inviteCode: string;
    email: string;
}

export const getInvitePendingVerification = (): IInvitePendingVerification | null => {
    try {
        const raw = localStorage.getItem(INVITE_PENDING_VERIFICATION_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.inviteCode && parsed?.email) {
            return {
                inviteCode: String(parsed.inviteCode),
                email: String(parsed.email).trim(),
            };
        }
    } catch {
        return null;
    }
    return null;
};

export const setInvitePendingVerification = (payload: IInvitePendingVerification): void => {
    localStorage.setItem(INVITE_PENDING_VERIFICATION_KEY, JSON.stringify({
        inviteCode: payload.inviteCode,
        email: payload.email.trim(),
    }));
};

export const clearInvitePendingVerification = (): void => {
    localStorage.removeItem(INVITE_PENDING_VERIFICATION_KEY);
};

/** Log out any existing session before invite signup / verify-email continues. */
export const logoutExistingUserForInviteAuth = async (
    hasLoggedInUser: boolean,
    logoutHandler: (to?: string) => Promise<void>,
    stayOnPath?: string,
): Promise<void> => {
    if (!hasLoggedInUser) return;
    await logoutHandler(stayOnPath);
};

export const getWorkspaceInitials = (name?: string | null): string => {
    if (!name?.trim()) return 'WS';

    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

    return words
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();
};

export const getInviteSignupTitle = (landing: IInviteLandingResponse): string => {
    const workspaceName = landing.invite?.workspace?.name;
    return workspaceName ? `Join ${workspaceName}` : 'Create your account';
};

export const getInviteSignupPageTitle = (): string => "You’re invited to join a workspace";

export const getInviteSignupPageSubtitle = (): string => 'Create your LeadDelta account to accept the invitation.';

export const getInviteSignupSubtitle = (landing: IInviteLandingResponse): string => {
    const workspaceName = landing.invite?.workspace?.name || 'a workspace';
    return `You've been invited to join ${workspaceName}. Create your LeadDelta account to start collaborating with your team.`;
};

export const getInviteSignupEmailStepSubtitle = (landing: IInviteLandingResponse): string => {
    const invitedEmail = landing.invite?.email;
    if (invitedEmail) {
        return `Use ${invitedEmail} to complete your registration.`;
    }
    return 'Register with the email address you were invited with.';
};

export const getInviteUnavailableMessage = (landing: IInviteLandingResponse): string => {
    switch (landing.status) {
        case 'expired':
            return 'This invite has expired.';
        case 'revoked':
            return 'This invite has been revoked and is no longer valid.';
        case 'not_found':
            return 'Invite not found. It may be invalid or the link is incorrect.';
        default:
            return landing.message;
    }
};

export type InviteUnavailableContactHint =
    | { type: 'plain'; text: string }
    | { type: 'email'; before: string; email: string; after: string };

export const getInviteUnavailableContactHint = (
    landing: IInviteLandingResponse,
): InviteUnavailableContactHint => {
    const adminEmail = landing.invite?.workspace?.adminEmail?.trim();

    if (!adminEmail) {
        switch (landing.status) {
            case 'expired':
                return { type: 'plain', text: 'Please ask your workspace admin for a new invite.' };
            case 'no_seats':
                return { type: 'plain', text: 'Please ask your workspace admin to resolve seat availability.' };
            default:
                return { type: 'plain', text: 'Please ask your workspace admin for assistance.' };
        }
    }

    switch (landing.status) {
        case 'expired':
            return {
                type: 'email',
                before: 'Contact your workspace admin at ',
                email: adminEmail,
                after: ' to request a new invite.',
            };
        case 'no_seats':
            return {
                type: 'email',
                before: 'Contact your workspace admin at ',
                email: adminEmail,
                after: ' to resolve seat availability.',
            };
        default:
            return {
                type: 'email',
                before: 'Contact your workspace admin at ',
                email: adminEmail,
                after: ' if you need assistance.',
            };
    }
};

export const parseStoredInviteValue = (raw: string | null): IInviteAuthContext | null => {
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw);

        if (parsed?.inviteCode && isInviteLandingResponse(parsed.landing)) {
            return parsed as IInviteAuthContext;
        }

        if (parsed?.invite?.code) {
            return {
                inviteCode: parsed.invite.code,
                landing: {
                    status: 'pending_registration',
                    message: '',
                    accepted: false,
                    user: 'new',
                    authMethod: 'register',
                    invite: {
                        email: parsed.invite?.to?.email ?? '',
                        workspace: parsed.invite?.workspace
                            ? {
                                name: parsed.invite.workspace.name,
                                short_name: '',
                                workosWorkspace: false,
                            }
                            : null,
                    },
                },
            };
        }
    } catch {
        return null;
    }

    return null;
};

/** @deprecated Legacy invite shape — kept for gradual migration */
export interface IInviteDetails {
    to: {
        email: string
    },
    active: boolean,
    resolved: boolean,
    _id: string,
    user: string,
    workspace: {
        _id: string,
        name: string
    },
    title: string,
    description: string,
    code: string,
    validUntil: string,
    createdAt: string,
    updatedAt: string,
}

/** @deprecated Legacy invite shape — kept for gradual migration */
export interface IInvite {
    invite: IInviteDetails;
}
